import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { type SelfSchedulingDayStructure, SelfSchedulingPreference } from "../../../utils/models";

const getRelativeFirstDayOfWeek = (currentDay: DateTime, sundayIsFirstDayOfWeek: boolean) => {
	const calcDay = currentDay.startOf("week");
	return calcDay.weekday % 7 !== 0 ? calcDay.minus({ days: 1 }) : calcDay;
	/*we currently want to display the first day as sunday regardless if the weekend style is USA or IL*/
	/*
	if (sundayIsFirstDayOfWeek ) {
		return calcDay.weekday % 7 !== 0 ? calcDay.minus({ days: 1 }) : calcDay;
	}
	return calcDay.weekday % 7 !== 1 ? calcDay.plus({day: 1}) : calcDay;
	*/
};
const amountOfDaysInWeek = 7;

type Props = {
	sundayIsFirstDayOfWeek: boolean;
	onDayClick?: (date: DateTime, updatedPreference: SelfSchedulingPreference) => void;
	maxWeekCount: number;
	daysData: Record<string, SelfSchedulingDayStructure>;
	targetWorkingDaysPerWeek: number;
};

/**
 * Custom hook to manage the state of the self-scheduling calendar.
 * @param sundayIsFirstDayOfWeek - Boolean indicating if Sunday is the first day of the week.
 * @param onDayClick - Callback function triggered when a day is clicked, receiving the date and updated preference.
 * @param maxWeekCount - Maximum number of weeks to display in the calendar.
 * @param daysData - A record mapping date strings to SelfSchedulingDayStructure objects.
 * @param targetWorkingDaysPerWeek - Target number of working days per week, default is 3, above which a warning may be displayed.
 */
const useSelfSchedulingCalendarState = ({ sundayIsFirstDayOfWeek, onDayClick, maxWeekCount, daysData, targetWorkingDaysPerWeek }: Props) => {
	const [firstDayOfWeeksWithOvertimeWarning, setFirstDayOfWeeksWithOvertimeWarning] = useState<DateTime[]>([]);
	const firstDate = useMemo(() => {
		const calcDay = DateTime.now().plus({ week: 1 });
		return getRelativeFirstDayOfWeek(calcDay, sundayIsFirstDayOfWeek);
	}, [sundayIsFirstDayOfWeek]);

	const isOverlappingYear = useMemo(() => {
		if (!firstDate) return false;
		const nextYear = firstDate.plus({ week: maxWeekCount });
		return firstDate.year !== nextYear.year;
	}, [firstDate, maxWeekCount]);

	const calendarLabel = useMemo(
		() => `${firstDate.toFormat(isOverlappingYear ? "MMM yyyy" : "MMM")} - ${firstDate.plus({ week: maxWeekCount }).toFormat("MMM yyyy")}`,
		[isOverlappingYear, firstDate, maxWeekCount],
	);

	const handleDayClick = useCallback(
		(date: DateTime, updatedPreference: SelfSchedulingPreference) => {
			const currentFirstDayOfWeek = getRelativeFirstDayOfWeek(date, sundayIsFirstDayOfWeek);

			let signedUpCount = updatedPreference === SelfSchedulingPreference.WANT_TO_WORK ? 1 : 0;
			for (let i = 0; i < amountOfDaysInWeek; i++) {
				const currentDateObject = daysData[currentFirstDayOfWeek.plus({ day: i }).toISO() as string];
				if (!currentDateObject) continue;
				if (currentDateObject.date == date) {
					continue; // Skip the clicked date
				}
				signedUpCount += currentDateObject?.dayPreference === SelfSchedulingPreference.WANT_TO_WORK ? 1 : 0;
			}
			if (signedUpCount > targetWorkingDaysPerWeek) {
				if (!firstDayOfWeeksWithOvertimeWarning.some((day) => day.startOf("day") === currentFirstDayOfWeek.startOf("day"))) {
					setFirstDayOfWeeksWithOvertimeWarning((prev) => [...prev, currentFirstDayOfWeek]);
				}
			} else {
				setFirstDayOfWeeksWithOvertimeWarning((prevState) => prevState.filter((value) => value !== currentFirstDayOfWeek));
			}
			onDayClick?.(date, updatedPreference);
		},
		[onDayClick, daysData, sundayIsFirstDayOfWeek, targetWorkingDaysPerWeek, firstDayOfWeeksWithOvertimeWarning],
	);

	return {
		firstDate,
		calendarLabel,
		handleDayClick,
		amountOfDaysInWeek,
		firstDayOfWeeksWithOvertimeWarning,
	};
};

export default useSelfSchedulingCalendarState;
